// src/components/HebrewDatePicker.tsx
// Hebrew calendar date picker — adapted from Firewatch HistoryPanel
// Pure JS Hebrew↔Gregorian conversion (no @hebcal/core needed on frontend)
// Shows Hebrew month names in English, day numbers with small Gregorian below

import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

// ══════════════════════════════════════════════════════════════
// Hebrew month definitions (civil-year order: Tishrei first)
// ══════════════════════════════════════════════════════════════

const HEBREW_MONTHS = [
  { num: 7,  name: 'Tishrei'  }, { num: 8,  name: 'Cheshvan' },
  { num: 9,  name: 'Kislev'   }, { num: 10, name: 'Teves'    },
  { num: 11, name: 'Shevat'   }, { num: 12, name: 'Adar'     },
  { num: 13, name: 'Adar II'  }, { num: 1,  name: 'Nissan'   },
  { num: 2,  name: 'Iyar'     }, { num: 3,  name: 'Sivan'    },
  { num: 4,  name: 'Tammuz'   }, { num: 5,  name: 'Av'       },
  { num: 6,  name: 'Elul'     },
];

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Approximate Gregorian month → Hebrew month
const GREG_TO_HEB_MONTH = [11, 12, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// ══════════════════════════════════════════════════════════════
// Pure-JS Hebrew ↔ Gregorian conversion (Fourmilab algorithm)
// ══════════════════════════════════════════════════════════════

function isHebrewLeapYear(hy: number) {
  return ((7 * hy) + 1) % 19 < 7;
}

const HEBREW_EPOCH_JD = 347995.5;
const GREGORIAN_EPOCH = 1721425.5;

// Hebrew gematria for numbers + month names in Hebrew
const GEMATRIA: Record<number, string> = {
  1:'א׳',2:'ב׳',3:'ג׳',4:'ד׳',5:'ה׳',6:'ו׳',7:'ז׳',8:'ח׳',9:'ט׳',10:'י׳',
  11:'י״א',12:'י״ב',13:'י״ג',14:'י״ד',15:'ט״ו',16:'ט״ז',17:'י״ז',18:'י״ח',19:'י״ט',20:'כ׳',
  21:'כ״א',22:'כ״ב',23:'כ״ג',24:'כ״ד',25:'כ״ה',26:'כ״ו',27:'כ״ז',28:'כ״ח',29:'כ״ט',30:'ל׳',
};

const HEBREW_MONTH_HE: Record<number, string> = {
  1:'ניסן',2:'אייר',3:'סיון',4:'תמוז',5:'אב',6:'אלול',
  7:'תשרי',8:'חשון',9:'כסלו',10:'טבת',11:'שבט',12:'אדר',13:'אדר ב׳',
};

function hebrewYearGematria(hy: number): string {
  // 5786 → תשפ״ו (drop the thousands, convert 786)
  const y = hy % 1000; // 786
  const parts: string[] = [];
  
  // Hundreds
  const hunds = Math.floor(y / 100);
  const hundredLetters: Record<number, string> = {1:'ק',2:'ר',3:'ש',4:'ת',5:'תק',6:'תר',7:'תש',8:'תת',9:'תתק'};
  if (hunds > 0 && hundredLetters[hunds]) parts.push(hundredLetters[hunds]);
  
  // Tens
  const t = Math.floor((y % 100) / 10);
  const tenLetters: Record<number, string> = {1:'י',2:'כ',3:'ל',4:'מ',5:'נ',6:'ס',7:'ע',8:'פ',9:'צ'};
  if (t > 0 && tenLetters[t]) parts.push(tenLetters[t]);
  
  // Ones
  const o = y % 10;
  const oneLetters: Record<number, string> = {1:'א',2:'ב',3:'ג',4:'ד',5:'ה',6:'ו',7:'ז',8:'ח',9:'ט'};
  if (o > 0 && oneLetters[o]) parts.push(oneLetters[o]);
  
  // Handle 15/16 special cases (use ט״ו / ט״ז instead of י״ה / י״ו)
  let s = parts.join('');
  // Insert ״ before last character
  if (s.length >= 2) s = s.slice(0, -1) + '״' + s.slice(-1);
  return s;
}

function formatHebrewGematria(val: HebrewDateValue): string {
  const day = GEMATRIA[val.hd] || String(val.hd);
  const month = HEBREW_MONTH_HE[val.hm] || '';
  const year = hebrewYearGematria(val.hy);
  return `${day} ${month} ${year}`;
}

function _hDelay1(year: number) {
  const months = Math.floor((235 * year - 234) / 19);
  const parts = 12084 + 13753 * months;
  let day = months * 29 + Math.floor(parts / 25920);
  if ((3 * (day + 1)) % 7 < 3) day++;
  return day;
}
function _hDelay2(year: number) {
  const last = _hDelay1(year - 1);
  const present = _hDelay1(year);
  const next = _hDelay1(year + 1);
  if (next - present === 356) return 2;
  if (present - last === 382) return 1;
  return 0;
}
function _hebrewToJD(year: number, month: number, day: number) {
  const months = isHebrewLeapYear(year) ? 13 : 12;
  let jd = HEBREW_EPOCH_JD + _hDelay1(year) + _hDelay2(year) + day + 1;
  if (month < 7) {
    for (let m = 7; m <= months; m++) jd += _hMonthDays(year, m);
    for (let m = 1; m < month; m++) jd += _hMonthDays(year, m);
  } else {
    for (let m = 7; m < month; m++) jd += _hMonthDays(year, m);
  }
  return jd;
}
function _hYearDays(year: number) {
  return _hebrewToJD(year + 1, 7, 1) - _hebrewToJD(year, 7, 1);
}
function _hMonthDays(year: number, month: number): number {
  if ([2, 4, 6, 10, 13].includes(month)) return 29;
  if (month === 12 && !isHebrewLeapYear(year)) return 29;
  if (month === 8 && _hYearDays(year) % 10 !== 5) return 29;
  if (month === 9 && _hYearDays(year) % 10 === 3) return 29;
  return 30;
}
function _gregToJD(year: number, month: number, day: number) {
  const isLeap = (year % 4 === 0) && (year % 100 !== 0 || year % 400 === 0);
  return GREGORIAN_EPOCH - 1 +
    365 * (year - 1) + Math.floor((year - 1) / 4) -
    Math.floor((year - 1) / 100) + Math.floor((year - 1) / 400) +
    Math.floor((367 * month - 362) / 12) +
    (month <= 2 ? 0 : (isLeap ? -1 : -2)) + day;
}
function _jdToGregorian(jd: number) {
  const wjd = Math.floor(jd - 0.5) + 0.5;
  const depoch = wjd - GREGORIAN_EPOCH;
  const quadricent = Math.floor(depoch / 146097);
  const dqc = depoch % 146097;
  const cent = Math.floor(dqc / 36524);
  const dcent = dqc % 36524;
  const quad = Math.floor(dcent / 1461);
  const dquad = dcent % 1461;
  const yindex = Math.floor(dquad / 365);
  let year = quadricent * 400 + cent * 100 + quad * 4 + yindex;
  if (!(cent === 4 || yindex === 4)) year++;
  const yearday = wjd - _gregToJD(year, 1, 1);
  const isLeap = (year % 4 === 0) && (year % 100 !== 0 || year % 400 === 0);
  const leapadj = wjd < _gregToJD(year, 3, 1) ? 0 : (isLeap ? 1 : 2);
  const month = Math.floor(((yearday + leapadj) * 12 + 373) / 367);
  const day = wjd - _gregToJD(year, month, 1) + 1;
  return new Date(year, month - 1, day);
}

/** Convert Hebrew date to Gregorian JS Date */
export function hebrewToGregorian(hy: number, hm: number, hd: number): Date {
  return _jdToGregorian(_hebrewToJD(hy, hm, hd));
}

/** Get the approximate current Hebrew year */
function currentHebrewYear(): number {
  return new Date().getFullYear() + 3760;
}

/** Local month length (safe defaults for variable months) */
function hebrewMonthLength(hy: number, hm: number): number {
  return _hMonthDays(hy, hm);
}

// ══════════════════════════════════════════════════════════════
// Format Gregorian date as YYYY-MM-DD
// ══════════════════════════════════════════════════════════════

function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ══════════════════════════════════════════════════════════════
// Exported types
// ══════════════════════════════════════════════════════════════

export interface HebrewDateValue {
  hy: number;
  hm: number;
  hd: number;
  gregDate: Date;
  label: string;
}

// ══════════════════════════════════════════════════════════════
// HebrewDateInput — single date dropdown with calendar
// ══════════════════════════════════════════════════════════════

export function HebrewDateInput({ label, value, onChange }: {
  label: string;
  value: HebrewDateValue | null;
  onChange: (val: HebrewDateValue) => void;
}) {
  const initHy = currentHebrewYear();
  const initHm = GREG_TO_HEB_MONTH[new Date().getMonth()] || 7;

  const [open, setOpen] = useState(false);
  const [hy, setHy] = useState(value?.hy || initHy);
  const [hm, setHm] = useState(value?.hm || initHm);
  const containerRef = useRef<HTMLDivElement>(null);

  const leap = isHebrewLeapYear(hy);
  const monthLen = hebrewMonthLength(hy, hm);
  const visMonths = HEBREW_MONTHS.filter(m => m.num !== 13 || leap);
  const monthName = HEBREW_MONTHS.find(m => m.num === hm)?.name || '';

  const gregOffset = useMemo(() => hebrewToGregorian(hy, hm, 1), [hy, hm]);
  const firstWday = gregOffset.getDay();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function goToPrev() {
    const idx = visMonths.findIndex(m => m.num === hm);
    if (idx > 0) { setHm(visMonths[idx - 1].num); }
    else {
      const prevLeap = isHebrewLeapYear(hy - 1);
      const prevMonths = HEBREW_MONTHS.filter(m => m.num !== 13 || prevLeap);
      setHy(hy - 1); setHm(prevMonths[prevMonths.length - 1].num);
    }
  }
  function goToNext() {
    const idx = visMonths.findIndex(m => m.num === hm);
    if (idx < visMonths.length - 1) { setHm(visMonths[idx + 1].num); }
    else { setHy(hy + 1); setHm(HEBREW_MONTHS[0].num); }
  }

  function selectDay(hd: number) {
    const g = hebrewToGregorian(hy, hm, hd);
    onChange({ hy, hm, hd, gregDate: g, label: `${hd} ${monthName} ${hy}` });
    setOpen(false);
  }

  function gregDayFor(hd: number) {
    return hebrewToGregorian(hy, hm, hd).getDate();
  }

  const selectedHd = value?.hm === hm && value?.hy === hy ? value.hd : null;
  const leadingBlanks = Array.from({ length: firstWday });

  return (
    <div ref={containerRef} className="relative">
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</div>

      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full px-2.5 py-1.5 rounded-md text-left flex items-center justify-between transition-all text-xs ${
          open ? 'border-blue-500 bg-slate-800' : 'border-slate-700/50 bg-slate-800/50'
        } border`}
      >
        <span className={value ? 'text-white' : 'text-slate-500'}>
          {value ? value.label : 'Select date...'}
        </span>
        <ChevronDown size={12} className="text-slate-500" />
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 rounded-xl border border-slate-600/50 bg-slate-800 shadow-2xl shadow-black/50 min-w-[270px]">
          {/* Month/Year nav */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-700/50">
            <div className="flex items-center gap-0.5">
              <button onClick={() => setHy(hy - 1)} className="p-1 rounded hover:bg-slate-700 text-slate-500 hover:text-white transition-colors" title="Previous year">
                <ChevronLeft size={10} />
              </button>
              <button onClick={goToPrev} className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Previous month">
                <ChevronLeft size={14} />
              </button>
            </div>
            <span className="text-sm font-semibold text-white">{monthName} {hy}</span>
            <div className="flex items-center gap-0.5">
              <button onClick={goToNext} className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Next month">
                <ChevronRight size={14} />
              </button>
              <button onClick={() => setHy(hy + 1)} className="p-1 rounded hover:bg-slate-700 text-slate-500 hover:text-white transition-colors" title="Next year">
                <ChevronRight size={10} />
              </button>
            </div>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 px-2.5 pt-2">
            {DAY_HEADERS.map(d => (
              <div key={d} className="text-center text-[9px] text-slate-600 pb-1 font-mono">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="px-2.5 pb-2.5">
            <div className="grid grid-cols-7 gap-0.5">
              {leadingBlanks.map((_, i) => <div key={`b${i}`} />)}
              {Array.from({ length: monthLen }, (_, i) => i + 1).map(hd => {
                const isSelected = hd === selectedHd;
                const gd = gregDayFor(hd);
                return (
                  <button
                    key={hd}
                    onClick={() => selectDay(hd)}
                    className={`py-1 px-0.5 rounded text-center transition-all ${
                      isSelected
                        ? 'bg-blue-600 border-blue-500'
                        : 'border-transparent hover:bg-slate-700/50'
                    } border`}
                  >
                    <div className={`text-[13px] font-semibold leading-none ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                      {hd}
                    </div>
                    <div className={`text-[8px] leading-none mt-0.5 font-mono ${isSelected ? 'text-blue-200' : 'text-slate-600'}`}>
                      {gd}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HebrewRangePicker — From/To with Apply button
// ══════════════════════════════════════════════════════════════

export default function HebrewRangePicker({ onApply }: {
  onApply: (startGreg: string, endGreg: string) => void;
}) {
  const [fromVal, setFromVal] = useState<HebrewDateValue | null>(null);
  const [toVal, setToVal] = useState<HebrewDateValue | null>(null);
  const canApply = fromVal && toVal && fromVal.gregDate.getTime() <= toVal.gregDate.getTime();
  function applyRange() {
    if (!fromVal || !toVal) return;
    onApply(toISODate(fromVal.gregDate), toISODate(toVal.gregDate));
  }
  function clearRange() {
    setFromVal(null);
    setToVal(null);
  }
  return (
    <div className="rounded-lg p-3 bg-slate-800/30 border border-slate-700/30">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <HebrewDateInput label="From" value={fromVal} onChange={setFromVal} />
        <HebrewDateInput label="To" value={toVal} onChange={setToVal} />
      </div>
      <div className="flex gap-2">
        <button
          onClick={applyRange}
          disabled={!canApply}
          className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${
            canApply
              ? 'bg-blue-600 text-white hover:bg-blue-500 cursor-pointer'
              : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
          }`}
        >
          Apply Range
        </button>
        <button
          onClick={clearRange}
          className="px-3 py-1.5 rounded-md text-xs text-slate-400 hover:text-white bg-slate-700/30 hover:bg-slate-700/50 transition-all"
        >
          Clear
        </button>
      </div>
      {fromVal && toVal && (
        <div className="mt-2 space-y-0.5 text-center">
          <div className="text-[11px] text-blue-300 font-hebrew" dir="rtl">
            {formatHebrewGematria(fromVal)} — {formatHebrewGematria(toVal)}
          </div>
          <div className="text-[10px] text-slate-500">
            {fromVal.gregDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' → '}
            {toVal.gregDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  );
}